import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isTabBarHidden } from './MobileTabBar.jsx';
import { useLocale } from '../hooks/useLocale.jsx';
import { PushService } from '../services/push-service.js';
import { analytics, logEvent } from '../firebase.js';

// 둘러보기 투어 (2.3.0) — 넘기는 카드 모달 두 개(만들기 4장 + 탭 스포트라이트)를
// 대신한다. 카드는 앱을 말로 설명하고 화면에 닿을 즈음엔 잊혔다. 투어는 실제
// 화면으로 이동해 진짜 버튼에 구멍을 뚫고 그 옆에 한 줄을 단다 (drape 의
// Tour.jsx 와 같은 모양). 예전 탭 투어는 만들기 카드가 먼저 끝나면서 같이
// "본 것" 으로 처리돼 모바일에선 사실상 한 번도 안 떴다.
//
// 키를 v1 로 새로 잡아서 기존 사용자도 한 번 본다 — 갤러리·디자이너 탭이 새로
// 생겼다. 자리가 바뀔 때만 올릴 것 (문구만 바뀌면 그대로).
const TOUR_KEY = 'archelier_tour_v1';
const FORCE_KEY = 'archelier_tour_force';
const REPLAY_EVENT = 'archelier:tour';

const MOBILE_MQ = '(max-width: 768px)';

// 앱 첫 2초는 JsSplash 가 덮고 있다 — 그 밑에서 시작하면 첫 단계를 못 본다.
const BOOT_AT = Date.now();
const SPLASH_MS = 2100;

// 단계마다 모바일/데스크톱 대상이 다르다 (모바일은 아래 탭바, 데스크톱은 헤더 탭 —
// 둘 다 MobileTabBar 의 TABS 로 그려서 칸이 같다).
// 대상이 null 이면 그 플랫폼에선 건너뛴다. 셀렉터가 여러 개면 처음 잡히는 것.
// 마지막은 일부러 만들기 — 투어가 끝나는 손 아래에 다음에 할 일이 있게.
const STEPS = [
  { route: '/create', body: 'tourCreate', pad: 6,
    mobile: ['[data-tour="create-upload"]', '.upload-step'],
    desktop: ['[data-tour="create-upload"]', '.upload-step'] },
  { route: '/gallery', body: 'tourGallery', pad: 4,
    mobile: ['[data-onboarding-id="tab-gallery"]'],
    desktop: ['[data-tour="nav-gallery"]'] },
  { route: '/gio', body: 'tourGio', pad: 4,
    mobile: ['[data-onboarding-id="tab-gio"]'],
    desktop: ['[data-tour="nav-gio"]'] },
  { route: '/designs', body: 'tourMy', pad: 4,
    mobile: ['[data-onboarding-id="tab-my"]'],
    desktop: ['[data-tour="nav-my"]'] },
  { route: '/create', body: 'tourStart', pad: 4,
    mobile: ['[data-onboarding-id="tab-create"] .floating-nav-fab', '[data-onboarding-id="tab-create"]'],
    desktop: ['[data-tour="nav-create"]'] },
];

// 투어를 다 봤는지 — 알림 허용을 이 뒤에 묻는다 (App.jsx, PushService).
export function isOnboardingDone() {
  return hasSeen(TOUR_KEY);
}

// 계정 화면의 "둘러보기 다시 보기".
export function replayTour() {
  try { sessionStorage.setItem(FORCE_KEY, '1'); } catch { /* ignore */ }
  window.dispatchEvent(new Event(REPLAY_EVENT));
}

function hasSeen(key) {
  try { return !!localStorage.getItem(key); } catch { return false; }
}
function markSeen(key) {
  try { localStorage.setItem(key, '1'); } catch { /* ignore */ }
}
function forced() {
  try {
    if (new URLSearchParams(window.location.search).get('tour') === '1') sessionStorage.setItem(FORCE_KEY, '1');
    return sessionStorage.getItem(FORCE_KEY) === '1';
  } catch { return false; }
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_MQ);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return isMobile;
}

// blocked: 환영 화면·로그인 창이 떠 있는 동안은 시작하지 않는다 (App.jsx).
export function Onboarding({ blocked = false }) {
  const { pathname } = useLocation();
  const [pending, setPending] = useState(() => forced() || !hasSeen(TOUR_KEY));

  useEffect(() => {
    const on = () => setPending(true);
    window.addEventListener(REPLAY_EVENT, on);
    return () => window.removeEventListener(REPLAY_EVENT, on);
  }, []);

  // 탭바가 있는 화면에서만 시작한다 — 랜딩·정책·공유 뷰(/s/*)·관리자 위에
  // 뜨면 가리킬 게 없다. 초대 링크로 처음 들어온 사람이 그걸 봤었다.
  const [booted, setBooted] = useState(() => Date.now() - BOOT_AT > SPLASH_MS);
  useEffect(() => {
    if (booted) return undefined;
    const tm = setTimeout(() => setBooted(true), Math.max(0, SPLASH_MS - (Date.now() - BOOT_AT)));
    return () => clearTimeout(tm);
  }, [booted]);
  const startable = booted && !blocked && !isTabBarHidden(pathname) && pathname !== '/admin';
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!pending || !startable || open) return;
    setOpen(true);
    logEvent(analytics, 'tour_start', { replay: hasSeen(TOUR_KEY) ? 1 : 0 });
  }, [pending, startable, open]);

  // 끝나든 건너뛰든 바로 알림 허용을 묻는다. 예전엔 생성을 맡기는 순간 물었는데,
  // 그땐 이미 앱을 내리는 중이라 창을 못 보거나 푸시가 오는 순간에야 허락을
  // 누르게 됐다. 로그인 전이면 App 이 로그인 직후에 묻는다.
  const finish = (how, step) => {
    logEvent(analytics, how === 'skip' ? 'tour_skip' : 'tour_complete', { step });
    markSeen(TOUR_KEY);
    try { sessionStorage.removeItem(FORCE_KEY); } catch { /* ignore */ }
    setPending(false);
    setOpen(false);
    PushService.askPermission();
  };

  // 환영 화면이 투어보다 늦게 뜰 수 있다 (로그인 상태 확인 뒤) — 그 동안은 내려 둔다.
  return open && startable ? <Tour onFinish={finish} /> : null;
}

// 대상이 아직 안 그려졌을 수 있다 (라우트 이동 직후, 지연 로딩). 잠깐 폴링하고,
// 끝내 없으면 null — 그 단계는 구멍 없이 가운데 캡션으로 뜬다.
function useTargetRect(selectors, key) {
  const [rect, setRect] = useState(null);
  const [radius, setRadius] = useState(16);
  const raf = useRef(0);
  useLayoutEffect(() => {
    let tries = 0;
    let alive = true;
    const settle = [];
    const find = () => selectors?.map((s) => document.querySelector(s)).find(Boolean);
    const measure = () => {
      if (!alive) return;
      const el = find();
      const r = el?.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) {
        // 대상의 모서리를 따라간다 — 알약 버튼엔 알약 모양 빛이 가게.
        const br = parseFloat(getComputedStyle(el).borderRadius) || 0;
        setRadius(br > Math.min(r.width, r.height) / 2 - 1 ? 999 : br + 6);
        setRect(r);
        // 찾은 직후엔 아직 자리가 흔들린다 (웹폰트·이미지·헤더 레이아웃). 몇 번 더 잰다.
        if (!settle.length) [150, 400, 900].forEach((ms) => settle.push(setTimeout(() => {
          const r2 = find()?.getBoundingClientRect();
          if (alive && r2 && r2.width > 0) setRect(r2);
        }, ms)));
        return;
      }
      if (tries++ < 90) raf.current = requestAnimationFrame(measure);
      else setRect(null);
    };
    setRect(null);
    measure();
    const on = () => { const r = find()?.getBoundingClientRect(); if (r) setRect(r); };
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      alive = false;
      settle.forEach(clearTimeout);
      cancelAnimationFrame(raf.current);
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return [rect, radius];
}

function Tour({ onFinish }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const steps = STEPS.filter((s) => (isMobile ? s.mobile : s.desktop));
  const [i, setI] = useState(0);
  const step = steps[i];
  const selectors = isMobile ? step?.mobile : step?.desktop;
  const [rect, radius] = useTargetRect(selectors, `${i}-${isMobile}`);

  // 화면부터 옮긴다 — 막 뒤에 있는 게 그 단계의 화면이어야 한다.
  useEffect(() => {
    if (step?.route) navigate(step.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!step) return null;

  const done = (how) => { navigate('/create'); onFinish(how, i + 1); };
  const next = () => { if (i + 1 >= steps.length) done('complete'); else setI(i + 1); };

  const pad = step.pad ?? 8;
  const hole = rect && {
    left: Math.max(rect.left - pad, 0),
    top: Math.max(rect.top - pad, 0),
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
  // 캡션은 자리가 있는 쪽에 — 아래 탭바 대상이면 위에, 헤더 대상이면 아래에.
  const below = hole ? hole.top + hole.height / 2 < window.innerHeight / 2 : true;
  const captionStyle = !hole ? undefined
    : below
      ? { top: Math.min(hole.top + hole.height + 14, window.innerHeight - 180) }
      : { bottom: window.innerHeight - hole.top + 14 };

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label={t('tourTitle')}>
      {hole
        ? <div className="tour-hole" style={{ ...hole, borderRadius: radius }} />
        : <div className="tour-scrim-full" />}

      <div className={`tour-caption${hole ? '' : ' centered'}`} style={captionStyle}>
        <p>{t(step.body)}</p>
        <div className="tour-caption-foot">
          <span className="tour-dots" aria-hidden="true">
            {steps.map((s, n) => <i key={s.body} className={n === i ? 'on' : ''} />)}
          </span>
          {/* 건너뛰기는 캡션 안에 — 화면 모서리에 두면 데스크톱에선 헤더의 로그인 버튼과,
              모바일에선 상단 칩과 겹친다. */}
          {i + 1 < steps.length && (
            <button type="button" className="tour-skip" onClick={() => done('skip')}>{t('onboardingSkip')}</button>
          )}
          <button type="button" className="tour-next" onClick={next}>
            {i + 1 >= steps.length ? t('onboardingStart') : t('onboardingNext')}
          </button>
        </div>
      </div>

      {/* 캡션 밖을 눌러도 다음으로 — 투어 중에 비친 버튼이 눌리지 않게. */}
      <button type="button" className="tour-advance" onClick={next} aria-label={t('onboardingNext')} />
    </div>
  );
}
